import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { MagentoProduct, MagentoConfigurableProduct } from '~layers/app/types/magento'

export function useStockNotification() {
  const { t } = useI18n()
  const { show: showNotification } = useNotification()

  const { initializeRecaptcha, executeRecaptcha } = useRecaptcha() || {}
  const { stockRequest } = useDataLayer()

  const email = ref('')
  const loading = ref(false)
  const showOOSForm = ref(false)
  const error = ref<string | null>(null)
  const recaptchaReady = ref(false)

  const initRecaptcha = async () => {
    if (!recaptchaReady.value && initializeRecaptcha) {
      await initializeRecaptcha()
      recaptchaReady.value = true
    }
  }

  const submit = async (
    productId: number | string,
    parentId?: number | string,
    product?: MagentoProduct | MagentoConfigurableProduct,
    itemSize?: string,
    sku?: string,
  ): Promise<boolean> => {
    if (!email.value) {
      showNotification({
        type: 'error',
        message: t('Please enter your email address'),
      })
      return false
    }

    loading.value = true
    error.value = null

    try {
      await initRecaptcha()
      const token = await executeRecaptcha?.('stock_notification')

      if (!token) {
        console.warn('No recaptcha token obtained')
      }

      const pId = typeof productId === 'string' ? parseInt(productId, 10) : productId
      const parId = parentId
        ? (typeof parentId === 'string' ? parseInt(parentId, 10) : parentId)
        : undefined

      const result = await useGraphqlMutation(
        'subscribeStockNotification',
        {
          email: email.value,
          productId: pId,
          ...(parId && { parentId: parId }),
        },
        {
          headers: {
            ...(token && { 'x-recaptcha': token }),
          },
        },
      )

      if (result.errors?.length) {
        error.value = result.errors[0].message
        showNotification({
          type: 'error',
          message: useMessageHandler(result.errors[0].message),
        })
        return false
      }

      if (product) {
        stockRequest(product, {
          item_size: itemSize || 'one-size',
          sku: sku,
        })
      }

      showNotification({
        type: 'success',
        message: t('Your subscription has been saved'),
      })
      showOOSForm.value = false
      email.value = ''
      return true
    }
    catch (e) {
      console.error('Stock Notification Error:', e)
      error.value = e.message || 'Unknown error'
      showNotification({
        type: 'error',
        message: t('Something went wrong. Please try again later.'),
      })
      return false
    }
    finally {
      loading.value = false
    }
  }

  const toggleForm = () => {
    showOOSForm.value = !showOOSForm.value
  }

  const resetForm = () => {
    email.value = ''
    error.value = null
    showOOSForm.value = false
  }

  return {
    email,
    loading,
    error,
    showOOSForm,
    submit,
    toggleForm,
    resetForm,
    initRecaptcha,
  }
}
